import { FC, HTMLAttributes } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button, GrowTherapyLogo, cn } from '@growtherapy/sprout-ui';

export type NavbarProps = {
  isDarkTheme: boolean;
  className?: string;
  onClickNeedHelpButton?: () => void;
  TranscriptionPillComponent?: React.FC;
  languageSwitcherComponent?: FC;
} & HTMLAttributes<HTMLDivElement>;

export function Navbar({
  isDarkTheme,
  className,
  onClickNeedHelpButton,
  TranscriptionPillComponent,
  languageSwitcherComponent: LanguageSwitcherComponent,
  ...props
}: NavbarProps): JSX.Element {
  const t = useTranslate();
  const showLanguageSwitcher = !!LanguageSwitcherComponent;
  const showNeedHelp = !!onClickNeedHelpButton;
  return (
    <div
      className={cn(
        {
          'bg-neutral-300 rebrand:bg-surface-default': !isDarkTheme,
          'bg-neutral-800 rebrand:bg-surface-inverse': isDarkTheme,
        },
        'relative flex items-center justify-between px-6 py-7 h-18',
        className,
      )}
      {...props}
    >
      <div className='flex content-start items-center gap-4'>
        <GrowTherapyLogo inverse={isDarkTheme} />
        {TranscriptionPillComponent && <TranscriptionPillComponent />}
      </div>
      <div className='flex items-center gap-350 sm:gap-450'>
        {LanguageSwitcherComponent && <LanguageSwitcherComponent />}
        {showNeedHelp && (
          <Button
            use='unstyled'
            className={cn(
              {
                'text-lilac-700 [&_span]:rebrand:text-action-primary-textLink': !isDarkTheme,
                'text-neutral-100 [&_span]:rebrand:!text-text-inverse': isDarkTheme,
              },
              'px-0 py-0 rebrand:px-0 rebrand:py-0 underline hover:brightness-85 active:brightness-70',
            )}
            onClick={onClickNeedHelpButton}
          >
            <span className={cn('hidden sm:block', { underline: showLanguageSwitcher })}>
              {t('telehealth-core:navigation.needHelp')}
            </span>
            <span className={cn('block sm:hidden', { underline: showLanguageSwitcher })}>?</span>
          </Button>
        )}
      </div>
    </div>
  );
}
